import { supabase } from '@/lib/supabase'
import { MetricPeriod } from '@/lib/utils'
import { AnalyticsData, DailyTotals } from '@/types'
import { getPreviousPeriod } from '@/lib/analytics/periodCalculations'
import {
  aggregateSnapshotMetricsByDate,
  buildSnapshotBaselinesByStudio
} from '@/lib/analytics/clientMetrics'

/**
 * Данные недельного плана из studio_weekly_plan_vs_fact
 */
interface WeeklyPlanVsFact {
  week_id: number
  week_start: string
  week_end: string
  week_number_in_period: number
  weekly_leads_plan?: number
  weekly_appointments_plan?: number
  weekly_new_visitors_plan?: number
  weekly_new_sales_plan?: number
  weekly_new_sales_amount_plan?: number
  weekly_renewals_amount_plan?: number
  weekly_upgrades_amount_plan?: number
  weekly_returning_clients_amount_plan?: number
  weekly_gross_revenue_plan?: number
  weekly_marketing_budget_plan?: number
  weekly_refunds_amount_plan?: number
  weekly_new_sales_aov_plan?: number
  weekly_new_subscriptions_count_plan?: number
  weekly_new_subscriptions_amount_plan?: number
  weekly_new_subscriptions_aov_plan?: number
  weekly_membership_fees_new_plan?: number
  weekly_membership_fees_subscription_upgrades_plan?: number
  weekly_membership_fees_subscription_returning_plan?: number
  booking_conversion_plan?: number
  visit_conversion_plan?: number
  sales_conversion_plan?: number
  new_subscriptions_conversion_plan?: number
  weekly_new_subscriptions_count_fact?: number
  weekly_new_subscriptions_amount_fact?: number
  [key: string]: any
}

/**
 * Данные плана студии за период (агрегированные из недельных планов)
 */
export interface PlanData {
  period_id: number
  period_start: string
  period_end: string
  weeks_in_period: number
  // План
  leads_count_plan: number
  appointments_count_plan: number
  new_visitors_count_plan: number
  new_sales_count_plan: number
  new_subscriptions_count_plan: number
  booking_conversion_plan: number
  visit_conversion_plan: number
  sales_conversion_plan: number
  new_subscriptions_conversion_plan?: number
  avg_new_sales_check_plan: number
  avg_new_subscription_check_plan: number
  avg_recurring_sales_check_plan?: number
  new_sales_amount_plan: number
  new_subscriptions_amount_plan: number
  membership_fees_new_plan?: number
  membership_fees_subscription_upgrades_plan?: number
  membership_fees_subscription_returning_plan?: number
  renewals_count_plan?: number
  renewals_amount_plan: number
  upgrades_count_plan?: number
  upgrades_amount_plan: number
  returning_clients_count_plan?: number
  returning_clients_amount_plan: number
  refunds_amount_plan: number
  total_gross_revenue_plan: number
  marketing_budget_plan: number
  // Факт (агрегированные из недельных данных)
  leads_count_fact?: number
  appointments_count_fact?: number
  new_visitors_count_fact?: number
  new_sales_count_fact?: number
  new_subscriptions_count_fact?: number
  new_sales_amount_fact?: number
  new_subscriptions_amount_fact?: number
  membership_fees_new_amount_fact?: number
  membership_fees_subscription_upgrades_amount_fact?: number
  membership_fees_subscription_returning_amount_fact?: number
  renewals_count_fact?: number
  renewals_amount_fact?: number
  upgrades_count_fact?: number
  upgrades_amount_fact?: number
  returning_clients_count_fact?: number
  returning_clients_amount_fact?: number
  refunds_amount_fact?: number
  total_gross_revenue_fact?: number
  marketing_budget_fact?: number
  [key: string]: any
}

/**
 * Данные для воронки продаж
 */
export interface FunnelData {
  leads: number
  appointments: number
  visits: number
  sales: number
}

/**
 * Полный набор данных аналитики студии за период
 */
export interface StudioAnalyticsData {
  analytics: AnalyticsData
  dailyData: DailyTotals[]
  conversionDailyData: DailyTotals[]
  nextPeriodHeadData: DailyTotals[]
  planData: PlanData | null
  previousPeriodData: PlanData | null
  funnelData: FunnelData
}

/**
 * Агрегированные данные аналитики по нескольким студиям
 */
export interface AggregatedAnalyticsData {
  analytics: AnalyticsData
  dailyData: DailyTotals[]
  conversionDailyData: DailyTotals[]
  nextPeriodHeadData: DailyTotals[]
  planData: PlanData | null
  previousPeriodData: PlanData | null
  funnelData: FunnelData
  studiosCount: number
  studioIds: number[]
}

/**
 * Сервис для работы с аналитикой студии
 * Содержит всю логику загрузки и обработки данных из Supabase
 */
class AnalyticsService {
  private metricPeriodsPromise: Promise<MetricPeriod[] | null> | null = null

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`${label} timed out after ${timeoutMs}ms`))
      }, timeoutMs)
    })

    try {
      return await Promise.race([promise, timeoutPromise])
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }

  private getDateDaysBefore(dateString: string, days: number): string {
    const date = new Date(dateString)
    date.setDate(date.getDate() - days)
    return date.toISOString().slice(0, 10)
  }

  private getDateDaysAfter(dateString: string, days: number): string {
    const date = new Date(dateString)
    date.setDate(date.getDate() + days)
    return date.toISOString().slice(0, 10)
  }

  private async getMetricPeriods(): Promise<MetricPeriod[] | null> {
    if (!this.metricPeriodsPromise) {
      this.metricPeriodsPromise = (async () => {
        const { data, error } = await supabase
          .from('metric_periods')
          .select('*')
          .order('period_start', { ascending: true })

        if (error || !data) {
          console.error('Error loading metric periods:', error)
          this.metricPeriodsPromise = null
          return null
        }

        return data as MetricPeriod[]
      })()
    }

    return this.metricPeriodsPromise
  }

  private async getPreviousMetricPeriod(currentPeriod: MetricPeriod): Promise<MetricPeriod | null> {
    const periods = await this.getMetricPeriods()
    if (!periods) {
      return null
    }

    return getPreviousPeriod(periods, currentPeriod)
  }

  /**
   * Загружает агрегированные данные за период из VIEW
   */
  private async loadPeriodTotals(studioId: number, periodId: number) {
    const { data, error } = await supabase
      .from('studio_period_totals')
      .select('*')
      .eq('studio_internal_id', studioId)
      .eq('period_id', periodId)
      .maybeSingle()
    
    if (error) {
      throw new Error(`Failed to load period totals: ${error.message}`)
    }

    // Если данных нет (период без записей), возвращаем пустой объект
    // formatMetrics обработает его с дефолтными значениями (|| 0)
    if (!data) {
      return {}
    }

    return data
  }

  /**
   * Загружает план на период из недельных планов
   * Агрегирует данные из studio_weekly_plan_vs_fact в месячный формат
   */
  private async loadPlanData(studioId: number, periodId: number): Promise<PlanData | null> {
    // Загружаем недельные планы для периода
    const { data: weeklyPlans, error } = await supabase
      .from('studio_weekly_plan_vs_fact')
      .select('*')
      .eq('studio_internal_id', studioId)
      .eq('period_id', periodId)
      .order('week_number_in_period')
    
    // План может отсутствовать - это нормально
    if (error || !weeklyPlans || weeklyPlans.length === 0) {
      return null
    }

    // Агрегируем недельные планы в месячный
    const typedWeeklyPlans = weeklyPlans as WeeklyPlanVsFact[]
    const firstWeek = typedWeeklyPlans[0]
    const aggregate = (field: string) => 
      typedWeeklyPlans.reduce((sum: number, w: any) => sum + (w[field] || 0), 0)
    
    const ratioConversion = (numeratorField: string, denominatorField: string) => {
      const denominator = aggregate(denominatorField)
      return denominator > 0 ? (aggregate(numeratorField) / denominator) * 100 : 0
    }
    const salesConversionPlan = (() => {
      const visitorsPlan = aggregate('weekly_new_visitors_plan')
      const salesPlan = aggregate('weekly_new_sales_plan') + aggregate('weekly_new_subscriptions_count_plan')
      return visitorsPlan > 0 ? (salesPlan / visitorsPlan) * 100 : 0
    })()

    return {
      period_id: periodId,
      period_start: firstWeek.week_start,
      period_end: typedWeeklyPlans[typedWeeklyPlans.length - 1].week_end,
      weeks_in_period: typedWeeklyPlans.length,
      
      // План (сумма недельных планов)
      leads_count_plan: aggregate('weekly_leads_plan'),
      appointments_count_plan: aggregate('weekly_appointments_plan'),
      new_visitors_count_plan: aggregate('weekly_new_visitors_plan'),
      new_sales_count_plan: aggregate('weekly_new_sales_plan'),
      new_subscriptions_count_plan: aggregate('weekly_new_subscriptions_count_plan'),
      new_sales_amount_plan: aggregate('weekly_new_sales_amount_plan'),
      new_subscriptions_amount_plan: aggregate('weekly_new_subscriptions_amount_plan'),
      membership_fees_new_plan: aggregate('weekly_membership_fees_new_plan'),
      membership_fees_subscription_upgrades_plan: aggregate('weekly_membership_fees_subscription_upgrades_plan'),
      membership_fees_subscription_returning_plan: aggregate('weekly_membership_fees_subscription_returning_plan'),
      subscription_renewals_count_plan: aggregate('weekly_subscription_renewals_count_plan'),
      subscription_renewals_amount_plan: aggregate('weekly_subscription_renewals_amount_plan'),
      subscription_upgrades_count_plan: aggregate('weekly_subscription_upgrades_count_plan'),
      subscription_upgrades_amount_plan: aggregate('weekly_subscription_upgrades_amount_plan'),
      subscription_returning_clients_count_plan: aggregate('weekly_subscription_returning_clients_count_plan'),
      subscription_returning_clients_amount_plan: aggregate('weekly_subscription_returning_clients_amount_plan'),
      renewals_count_plan: aggregate('weekly_renewals_count_plan'),
      renewals_amount_plan: aggregate('weekly_renewals_amount_plan'),
      upgrades_count_plan: aggregate('weekly_upgrades_count_plan'),
      upgrades_amount_plan: aggregate('weekly_upgrades_amount_plan'),
      returning_clients_count_plan: aggregate('weekly_returning_clients_count_plan'),
      returning_clients_amount_plan: aggregate('weekly_returning_clients_amount_plan'),
      refunds_amount_plan: aggregate('weekly_refunds_amount_plan'),
      total_gross_revenue_plan: aggregate('weekly_gross_revenue_plan'),
      marketing_budget_plan: aggregate('weekly_marketing_budget_plan'),
      // AOV не суммируется, считаем из итоговых сумм и количества
      avg_new_sales_check_plan: (() => {
        const count = aggregate('weekly_new_sales_plan')
        const amount = aggregate('weekly_new_sales_amount_plan')
        return count > 0 ? amount / count : (firstWeek.weekly_new_sales_aov_plan || 0)
      })(),
      avg_new_subscription_check_plan: (() => {
        const count = aggregate('weekly_new_subscriptions_count_plan')
        const amount = aggregate('weekly_new_subscriptions_amount_plan')
        return count > 0 ? amount / count : (firstWeek.weekly_new_subscriptions_aov_plan || 0)
      })(),
      avg_recurring_sales_check_plan: (() => {
        const count =
          aggregate('weekly_renewals_count_plan') +
          aggregate('weekly_upgrades_count_plan') +
          aggregate('weekly_returning_clients_count_plan') +
          aggregate('weekly_subscription_renewals_count_plan') +
          aggregate('weekly_subscription_upgrades_count_plan') +
          aggregate('weekly_subscription_returning_clients_count_plan')
        const amount =
          aggregate('weekly_renewals_amount_plan') +
          aggregate('weekly_upgrades_amount_plan') +
          aggregate('weekly_returning_clients_amount_plan') +
          aggregate('weekly_subscription_renewals_amount_plan') +
          aggregate('weekly_subscription_upgrades_amount_plan') +
          aggregate('weekly_subscription_returning_clients_amount_plan')
        return count > 0 ? amount / count : 0
      })(),
      
      booking_conversion_plan: ratioConversion('weekly_appointments_plan', 'weekly_leads_plan'),
      visit_conversion_plan: ratioConversion('weekly_new_visitors_plan', 'weekly_appointments_plan'),
      sales_conversion_plan: salesConversionPlan,
      
      // Факт (сумма недельных фактов)
      leads_count_fact: aggregate('weekly_leads_fact'),
      appointments_count_fact: aggregate('weekly_appointments_fact'),
      new_visitors_count_fact: aggregate('weekly_new_visitors_fact'),
      new_sales_count_fact: aggregate('weekly_new_sales_fact'),
      new_subscriptions_count_fact: aggregate('weekly_new_subscriptions_count_fact'),
      new_sales_amount_fact: aggregate('weekly_new_sales_amount_fact'),
      new_subscriptions_amount_fact: aggregate('weekly_new_subscriptions_amount_fact'),
      membership_fees_new_amount_fact: aggregate('weekly_membership_fees_new_amount_fact'),
      membership_fees_subscription_upgrades_amount_fact: aggregate('weekly_membership_fees_subscription_upgrades_amount_fact'),
      membership_fees_subscription_returning_amount_fact: aggregate('weekly_membership_fees_subscription_returning_amount_fact'),
      subscription_renewals_count_fact: aggregate('weekly_subscription_renewals_count_fact'),
      subscription_renewals_amount_fact: aggregate('weekly_subscription_renewals_amount_fact'),
      subscription_upgrades_count_fact: aggregate('weekly_subscription_upgrades_count_fact'),
      subscription_upgrades_amount_fact: aggregate('weekly_subscription_upgrades_amount_fact'),
      subscription_returning_clients_count_fact: aggregate('weekly_subscription_returning_clients_count_fact'),
      subscription_returning_clients_amount_fact: aggregate('weekly_subscription_returning_clients_amount_fact'),
      renewals_count_fact: aggregate('weekly_renewals_count_fact'),
      renewals_amount_fact: aggregate('weekly_renewals_amount_fact'),
      upgrades_count_fact: aggregate('weekly_upgrades_count_fact'),
      upgrades_amount_fact: aggregate('weekly_upgrades_amount_fact'),
      returning_clients_count_fact: aggregate('weekly_returning_clients_count_fact'),
      returning_clients_amount_fact: aggregate('weekly_returning_clients_amount_fact'),
      refunds_amount_fact: aggregate('weekly_refunds_amount_fact'),
      total_gross_revenue_fact: aggregate('weekly_gross_revenue_fact'),
      marketing_budget_fact: aggregate('weekly_marketing_expenses_amount_fact')
    } as PlanData
  }

  /**
   * Загружает детальные дневные данные за период
   */
  private async loadDailyData(studioId: number, periodId: number): Promise<DailyTotals[]> {
    const { data, error } = await supabase
      .from('studio_daily_totals')
      .select('*')
      .eq('studio_internal_id', studioId)
      .eq('period_id', periodId)
      .order('metric_date')

    if (error) {
      throw new Error(`Failed to load daily data: ${error.message}`)
    }

    return (data || []) as DailyTotals[]
  }

  /**
   * Загружает "хвост" до начала периода для расчета скользящих конверсий.
   * Нужны только дни ДО period_start, внутри периода данные уже есть в loadDailyData.
   */
  private async loadConversionTailData(
    studioId: number,
    periodStart: string,
    windowSize: number = 7
  ): Promise<DailyTotals[]> {
    const tailDays = Math.max(0, windowSize - 1)
    if (tailDays === 0) return []

    const tailStart = this.getDateDaysBefore(periodStart, tailDays)

    const { data, error } = await supabase
      .from('studio_daily_totals')
      .select('*')
      .eq('studio_internal_id', studioId)
      .gte('metric_date', tailStart)
      .lt('metric_date', periodStart)
      .order('metric_date')

    if (error) {
      throw new Error(`Failed to load conversion tail data: ${error.message}`)
    }

    return (data || []) as DailyTotals[]
  }

  /**
   * Загружает "голову" следующего периода (дни ПОСЛЕ period_end)
   * для корректного определения состояния на конец выбранного диапазона.
   */
  private async loadNextPeriodHeadData(
    studioId: number,
    periodEnd: string,
    days: number = 2
  ): Promise<DailyTotals[]> {
    const headDays = Math.max(0, days)
    if (headDays === 0) return []

    const headStart = this.getDateDaysAfter(periodEnd, 1)
    const headEnd = this.getDateDaysAfter(periodEnd, headDays)

    const { data, error } = await supabase
      .from('studio_daily_totals')
      .select('*')
      .eq('studio_internal_id', studioId)
      .gte('metric_date', headStart)
      .lte('metric_date', headEnd)
      .order('metric_date')

    if (error) {
      throw new Error(`Failed to load next period head data: ${error.message}`)
    }

    return (data || []) as DailyTotals[]
  }

  /**
   * Рассчитывает данные для воронки продаж на основе дневных данных
   */
  private calculateFunnelData(dailyData: DailyTotals[]): FunnelData {
    return {
      leads: dailyData.reduce((sum, day) => sum + (day.leads_count || 0), 0),
      appointments: dailyData.reduce((sum, day) => sum + (day.appointments_count || 0), 0),
      visits: dailyData.reduce((sum, day) => sum + (day.new_visitors_count || 0), 0),
      sales: dailyData.reduce((sum, day) => sum + (day.new_sales_count || 0) + (day.new_subscriptions_count || 0), 0)
    }
  }

  /**
   * Рассчитывает маркетинговые расходы за период
   */
  private calculateMarketingExpenses(dailyData: DailyTotals[]): number {
    return dailyData.reduce(
      (sum, day) => sum + (day.marketing_expenses_amount || 0), 
      0
    )
  }

  /**
   * Формирует объект метрик на основе агрегированных данных
   * @param totals - агрегированные данные периода
   * @param marketingExpenses - маркетинговые расходы
   * @param previousPeriodTotals - агрегированные данные предыдущего периода (опционально)
   */
  private formatMetrics(
    totals: any, 
    marketingExpenses: number,
    previousPeriodTotals?: any
  ) {
    // Текущее количество клиентов на конец периода
    const currentClientsCount = totals.last_total_clients_count || 0
    
    // Отклонение клиентов относительно предыдущего периода
    // Если есть данные предыдущего периода - считаем как (текущее - предыдущее)
    // Если нет - используем изменение внутри периода из VIEW
    let clientsGrowth: number | undefined = undefined
    if (previousPeriodTotals && previousPeriodTotals.last_total_clients_count !== undefined) {
      clientsGrowth = currentClientsCount - (previousPeriodTotals.last_total_clients_count || 0)
    } else if (totals.clients_growth !== undefined) {
      // Fallback на значение из VIEW (изменение внутри периода)
      clientsGrowth = totals.clients_growth
    }
    
    const metrics = {
      total_revenue: totals.total_gross_revenue || 0,
      new_sales_amount: (totals.total_new_sales_amount || 0) + (totals.total_membership_fees_new_amount || 0) + (totals.total_new_subscriptions_amount || 0),
      renewals_amount: totals.total_renewals_amount || 0,
      returning_clients_amount: totals.total_returning_clients_amount || 0,
      private_sessions_amount: totals.total_private_sessions_amount || 0,
      
      total_visitors: totals.total_new_visitors || 0,
      total_sales: (totals.total_new_sales_count || 0) + (totals.total_new_subscriptions_count || 0),
      renewals_count: totals.total_renewals_count || 0,
      returning_clients_count: totals.total_returning_clients_count || 0,
      
      // Метрики для подписок
      new_subscriptions_count: totals.total_new_subscriptions_count || 0,
      new_subscriptions_amount: totals.total_new_subscriptions_amount || 0,
      membership_fees_new_amount: totals.total_membership_fees_new_amount || 0,
      membership_fees_subscription_upgrades_amount: totals.total_membership_fees_subscription_upgrades || 0,
      membership_fees_subscription_returning_amount: totals.total_membership_fees_subscription_returning || 0,
      subscription_renewals_count: totals.total_subscription_renewals_count || 0,
      subscription_renewals_amount: totals.total_subscription_renewals_amount || 0,
      subscription_upgrades_count: totals.total_subscription_upgrades_count || 0,
      subscription_upgrades_amount: totals.total_subscription_upgrades_amount || 0,
      subscription_returning_clients_count: totals.total_subscription_returning_clients_count || 0,
      subscription_returning_clients_amount: totals.total_subscription_returning_clients_amount || 0,
      
      total_clients_count: currentClientsCount,
      clients_growth: clientsGrowth,
      
      marketing_expenses: marketingExpenses,
      
      avg_sale_amount: 0,
      conversion_rate: 0
    }

    // Средний чек новых продаж считаем без членских взносов: взнос анализируется отдельной метрикой.
    const totalNewCount = metrics.total_sales || 0
    const totalNewAmount = (metrics.new_sales_amount || 0) - (metrics.membership_fees_new_amount || 0)
    metrics.avg_sale_amount = totalNewCount > 0 ? totalNewAmount / totalNewCount : 0
    metrics.conversion_rate = metrics.total_visitors > 0
      ? (totalNewCount / metrics.total_visitors) * 100
      : 0

    return metrics
  }

  /**
   * Загружает данные предыдущего периода для сравнения
   */
  private async loadPreviousPeriodData(
    studioId: number, 
    currentPeriod: MetricPeriod
  ): Promise<PlanData | null> {
    try {
      const previousPeriod = await this.getPreviousMetricPeriod(currentPeriod)
      if (!previousPeriod) {
        return null
      }

      // Загружаем план предыдущего периода
      const prevPlan = await this.loadPlanData(studioId, previousPeriod.period_id)
      
      if (!prevPlan) {
        return null
      }

      // Загружаем дневные данные предыдущего периода для маркетингового бюджета
      const prevDaily = await this.loadDailyData(studioId, previousPeriod.period_id)
      const prevMarketingBudget = this.calculateMarketingExpenses(prevDaily)
      
      return {
        ...prevPlan,
        marketing_budget_fact: prevMarketingBudget
      }
    } catch (err) {
      console.error('Error loading previous period data:', err)
      return null
    }
  }

  /**
   * Загружает totals предыдущего периода для расчета clients_growth
   */
  private async loadPreviousPeriodTotals(
    studioId: number, 
    currentPeriod: MetricPeriod
  ): Promise<any | null> {
    try {
      const previousPeriod = await this.getPreviousMetricPeriod(currentPeriod)
      if (!previousPeriod) {
        return null
      }

      // Загружаем totals предыдущего периода
      return await this.loadPeriodTotals(studioId, previousPeriod.period_id)
    } catch (err) {
      console.error('Error loading previous period totals:', err)
      return null
    }
  }

  /**
   * Загружает все данные аналитики студии за период
   * Главный метод сервиса
   * 
   * @param studioId - ID студии
   * @param period - Период для загрузки
   * @returns Полный набор данных аналитики
   */
  async loadPeriodAnalytics(
    studioId: number,
    period: MetricPeriod
  ): Promise<StudioAnalyticsData> {
    // 1. Загружаем агрегированные данные за период
    const totals = await this.loadPeriodTotals(studioId, period.period_id)

    // 2. Загружаем план на период
    const planData = await this.loadPlanData(studioId, period.period_id)

    // 3. Загружаем детальные данные по дням
    const dailyData = await this.loadDailyData(studioId, period.period_id)
    const conversionTailData = await this.loadConversionTailData(studioId, period.period_start, 7)
    const nextPeriodHeadData = await this.loadNextPeriodHeadData(studioId, period.period_end, 2)
    const conversionDailyData = [...conversionTailData, ...dailyData]

    // 4. Рассчитываем данные для воронки продаж
    const funnelData = this.calculateFunnelData(dailyData)

    // 5. Загружаем данные предыдущего периода для сравнения
    const previousPeriodData = await this.loadPreviousPeriodData(studioId, period)
    
    // 6. Загружаем totals предыдущего периода для расчета clients_growth
    const previousPeriodTotals = await this.loadPreviousPeriodTotals(studioId, period)

    // 7. Рассчитываем маркетинговые расходы
    const marketingExpenses = this.calculateMarketingExpenses(dailyData)

    // 8. Формируем объект аналитики с метриками
    const metrics = this.formatMetrics(totals, marketingExpenses, previousPeriodTotals)

    return {
      analytics: { metrics },
      dailyData,
      conversionDailyData,
      nextPeriodHeadData,
      planData,
      previousPeriodData,
      funnelData
    }
  }

  /**
   * Обновляет только дневные данные (для частичного обновления)
   */
  async refreshDailyData(studioId: number, periodId: number): Promise<DailyTotals[]> {
    return this.loadDailyData(studioId, periodId)
  }

  /**
   * Обновляет только план (для частичного обновления)
   */
  async refreshPlanData(studioId: number, periodId: number): Promise<PlanData | null> {
    return this.loadPlanData(studioId, periodId)
  }

  /**
   * Агрегирует планы нескольких студий
   */
  private aggregatePlanData(studiosData: StudioAnalyticsData[], periodId: number): PlanData | null {
    const plansToAggregate = studiosData
      .map(sd => sd.planData)
      .filter((plan): plan is PlanData => plan !== null)

    if (plansToAggregate.length === 0) {
      return null
    }

    // Берем период из первого плана (у всех должен быть одинаковый)
    const firstPlan = plansToAggregate[0]

    // Суммируем планы
    const aggregatedPlan: PlanData = {
      period_id: periodId,
      period_start: firstPlan.period_start,
      period_end: firstPlan.period_end,
      weeks_in_period: firstPlan.weeks_in_period,
      
      // Суммируем план
      leads_count_plan: plansToAggregate.reduce((sum, p) => sum + (p.leads_count_plan || 0), 0),
      appointments_count_plan: plansToAggregate.reduce((sum, p) => sum + (p.appointments_count_plan || 0), 0),
      new_visitors_count_plan: plansToAggregate.reduce((sum, p) => sum + (p.new_visitors_count_plan || 0), 0),
      new_sales_count_plan: plansToAggregate.reduce((sum, p) => sum + (p.new_sales_count_plan || 0), 0),
      new_subscriptions_count_plan: plansToAggregate.reduce((sum, p) => sum + (p.new_subscriptions_count_plan || 0), 0),
      new_sales_amount_plan: plansToAggregate.reduce((sum, p) => sum + (p.new_sales_amount_plan || 0), 0),
      new_subscriptions_amount_plan: plansToAggregate.reduce((sum, p) => sum + (p.new_subscriptions_amount_plan || 0), 0),
      membership_fees_new_plan: plansToAggregate.reduce((sum, p) => sum + (p.membership_fees_new_plan || 0), 0),
      membership_fees_subscription_upgrades_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).membership_fees_subscription_upgrades_plan || 0), 0),
      membership_fees_subscription_returning_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).membership_fees_subscription_returning_plan || 0), 0),
      subscription_renewals_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_renewals_count_plan || 0), 0),
      subscription_renewals_amount_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_renewals_amount_plan || 0), 0),
      subscription_upgrades_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_upgrades_count_plan || 0), 0),
      subscription_upgrades_amount_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_upgrades_amount_plan || 0), 0),
      subscription_returning_clients_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_returning_clients_count_plan || 0), 0),
      subscription_returning_clients_amount_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_returning_clients_amount_plan || 0), 0),
      renewals_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).renewals_count_plan || 0), 0),
      renewals_amount_plan: plansToAggregate.reduce((sum, p) => sum + (p.renewals_amount_plan || 0), 0),
      upgrades_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).upgrades_count_plan || 0), 0),
      upgrades_amount_plan: plansToAggregate.reduce((sum, p) => sum + (p.upgrades_amount_plan || 0), 0),
      returning_clients_count_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).returning_clients_count_plan || 0), 0),
      returning_clients_amount_plan: plansToAggregate.reduce((sum, p) => sum + (p.returning_clients_amount_plan || 0), 0),
      refunds_amount_plan: plansToAggregate.reduce((sum, p) => sum + ((p as any).refunds_amount_plan || 0), 0),
      total_gross_revenue_plan: plansToAggregate.reduce((sum, p) => sum + (p.total_gross_revenue_plan || 0), 0),
      marketing_budget_plan: plansToAggregate.reduce((sum, p) => sum + (p.marketing_budget_plan || 0), 0),
      
      booking_conversion_plan: plansToAggregate.reduce((sum, p) => sum + (p.leads_count_plan || 0), 0) > 0
        ? (
            plansToAggregate.reduce((sum, p) => sum + (p.appointments_count_plan || 0), 0) /
            plansToAggregate.reduce((sum, p) => sum + (p.leads_count_plan || 0), 0)
          ) * 100
        : 0,
      visit_conversion_plan: plansToAggregate.reduce((sum, p) => sum + (p.appointments_count_plan || 0), 0) > 0
        ? (
            plansToAggregate.reduce((sum, p) => sum + (p.new_visitors_count_plan || 0), 0) /
            plansToAggregate.reduce((sum, p) => sum + (p.appointments_count_plan || 0), 0)
          ) * 100
        : 0,
      sales_conversion_plan: (() => {
        const visitorsPlan = plansToAggregate.reduce((sum, p) => sum + (p.new_visitors_count_plan || 0), 0)
        const totalNewSalesPlan = plansToAggregate.reduce(
          (sum, p) => sum + (p.new_sales_count_plan || 0) + ((p as any).new_subscriptions_count_plan || 0),
          0
        )
        return visitorsPlan > 0 ? (totalNewSalesPlan / visitorsPlan) * 100 : 0
      })(),
      new_subscriptions_conversion_plan: this.calculateWeightedAverage(
        plansToAggregate,
        'new_subscriptions_conversion_plan',
        'new_visitors_count_plan'
      ),
      avg_new_sales_check_plan: this.calculateWeightedAverage(
        plansToAggregate,
        'avg_new_sales_check_plan',
        'new_sales_count_plan'
      ),
      avg_new_subscription_check_plan: this.calculateWeightedAverage(
        plansToAggregate,
        'avg_new_subscription_check_plan',
        'new_subscriptions_count_plan'
      ),
      avg_recurring_sales_check_plan: (() => {
        const count = plansToAggregate.reduce((sum, p) => (
          sum
          + ((p as any).renewals_count_plan || 0)
          + ((p as any).upgrades_count_plan || 0)
          + ((p as any).returning_clients_count_plan || 0)
          + ((p as any).subscription_renewals_count_plan || 0)
          + ((p as any).subscription_upgrades_count_plan || 0)
          + ((p as any).subscription_returning_clients_count_plan || 0)
        ), 0)
        const amount = plansToAggregate.reduce((sum, p) => (
          sum
          + (p.renewals_amount_plan || 0)
          + (p.upgrades_amount_plan || 0)
          + (p.returning_clients_amount_plan || 0)
          + ((p as any).subscription_renewals_amount_plan || 0)
          + ((p as any).subscription_upgrades_amount_plan || 0)
          + ((p as any).subscription_returning_clients_amount_plan || 0)
        ), 0)
        return count > 0 ? amount / count : 0
      })(),
      
      // Суммируем факт
      leads_count_fact: plansToAggregate.reduce((sum, p) => sum + (p.leads_count_fact || 0), 0),
      appointments_count_fact: plansToAggregate.reduce((sum, p) => sum + (p.appointments_count_fact || 0), 0),
      new_visitors_count_fact: plansToAggregate.reduce((sum, p) => sum + (p.new_visitors_count_fact || 0), 0),
      new_sales_count_fact: plansToAggregate.reduce((sum, p) => sum + (p.new_sales_count_fact || 0), 0),
      new_subscriptions_count_fact: plansToAggregate.reduce((sum, p) => sum + (p.new_subscriptions_count_fact || 0), 0),
      new_sales_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.new_sales_amount_fact || 0), 0),
      new_subscriptions_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.new_subscriptions_amount_fact || 0), 0),
      membership_fees_new_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.membership_fees_new_amount_fact || 0), 0),
      membership_fees_subscription_upgrades_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).membership_fees_subscription_upgrades_amount_fact || 0), 0),
      membership_fees_subscription_returning_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).membership_fees_subscription_returning_amount_fact || 0), 0),
      subscription_renewals_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_renewals_count_fact || 0), 0),
      subscription_renewals_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_renewals_amount_fact || 0), 0),
      subscription_upgrades_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_upgrades_count_fact || 0), 0),
      subscription_upgrades_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_upgrades_amount_fact || 0), 0),
      subscription_returning_clients_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_returning_clients_count_fact || 0), 0),
      subscription_returning_clients_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).subscription_returning_clients_amount_fact || 0), 0),
      renewals_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).renewals_count_fact || 0), 0),
      renewals_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.renewals_amount_fact || 0), 0),
      upgrades_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).upgrades_count_fact || 0), 0),
      upgrades_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.upgrades_amount_fact || 0), 0),
      returning_clients_count_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).returning_clients_count_fact || 0), 0),
      returning_clients_amount_fact: plansToAggregate.reduce((sum, p) => sum + (p.returning_clients_amount_fact || 0), 0),
      refunds_amount_fact: plansToAggregate.reduce((sum, p) => sum + ((p as any).refunds_amount_fact || 0), 0),
      total_gross_revenue_fact: plansToAggregate.reduce((sum, p) => sum + (p.total_gross_revenue_fact || 0), 0),
      marketing_budget_fact: plansToAggregate.reduce((sum, p) => sum + (p.marketing_budget_fact || 0), 0)
    }

    return aggregatedPlan
  }

  /**
   * Рассчитывает средневзвешенное значение
   */
  private calculateWeightedAverage(
    plans: PlanData[],
    valueField: keyof PlanData,
    weightField: keyof PlanData
  ): number {
    let totalValue = 0
    let totalWeight = 0

    plans.forEach(plan => {
      const value = plan[valueField] as number || 0
      const weight = plan[weightField] as number || 0
      
      totalValue += value * weight
      totalWeight += weight
    })

    return totalWeight > 0 ? totalValue / totalWeight : 0
  }

  /**
   * Агрегирует метрики нескольких студий
   * @param studiosData - данные всех студий
   * @param previousStudiosData - данные всех студий за предыдущий период (опционально)
   */
  private aggregateMetrics(
    studiosData: StudioAnalyticsData[],
    previousStudiosData?: StudioAnalyticsData[]
  ): AnalyticsData {
    const totalMetrics = {
      total_revenue: 0,
      new_sales_amount: 0,
      renewals_amount: 0,
      returning_clients_amount: 0,
      private_sessions_amount: 0,
      
      total_visitors: 0,
      total_sales: 0,
      renewals_count: 0,
      returning_clients_count: 0,
      
      new_subscriptions_count: 0,
      new_subscriptions_amount: 0,
      membership_fees_new_amount: 0,
      membership_fees_subscription_upgrades_amount: 0,
      membership_fees_subscription_returning_amount: 0,
      subscription_renewals_count: 0,
      subscription_renewals_amount: 0,
      subscription_upgrades_count: 0,
      subscription_upgrades_amount: 0,
      subscription_returning_clients_count: 0,
      subscription_returning_clients_amount: 0,
      
      total_clients_count: 0,
      clients_growth: undefined as number | undefined,
      marketing_expenses: 0,
      
      avg_sale_amount: 0,
      conversion_rate: 0
    }

    // Суммируем все метрики
    studiosData.forEach(studioData => {
      const metrics = studioData.analytics.metrics
      
      totalMetrics.total_revenue += metrics.total_revenue || 0
      totalMetrics.new_sales_amount += metrics.new_sales_amount || 0
      totalMetrics.renewals_amount += metrics.renewals_amount || 0
      totalMetrics.returning_clients_amount += metrics.returning_clients_amount || 0
      totalMetrics.private_sessions_amount += metrics.private_sessions_amount || 0
      
      totalMetrics.total_visitors += metrics.total_visitors || 0
      totalMetrics.total_sales += metrics.total_sales || 0
      totalMetrics.renewals_count += metrics.renewals_count || 0
      totalMetrics.returning_clients_count += metrics.returning_clients_count || 0
      
      totalMetrics.new_subscriptions_count += metrics.new_subscriptions_count || 0
      totalMetrics.new_subscriptions_amount += metrics.new_subscriptions_amount || 0
      totalMetrics.membership_fees_new_amount += metrics.membership_fees_new_amount || 0
      totalMetrics.membership_fees_subscription_upgrades_amount += metrics.membership_fees_subscription_upgrades_amount || 0
      totalMetrics.membership_fees_subscription_returning_amount += metrics.membership_fees_subscription_returning_amount || 0
      totalMetrics.subscription_renewals_count += metrics.subscription_renewals_count || 0
      totalMetrics.subscription_renewals_amount += metrics.subscription_renewals_amount || 0
      totalMetrics.subscription_upgrades_count += metrics.subscription_upgrades_count || 0
      totalMetrics.subscription_upgrades_amount += metrics.subscription_upgrades_amount || 0
      totalMetrics.subscription_returning_clients_count += metrics.subscription_returning_clients_count || 0
      totalMetrics.subscription_returning_clients_amount += metrics.subscription_returning_clients_amount || 0
      
      totalMetrics.total_clients_count += metrics.total_clients_count || 0
      totalMetrics.marketing_expenses += metrics.marketing_expenses || 0
    })
    
    // Рассчитываем clients_growth относительно предыдущего периода
    if (previousStudiosData && previousStudiosData.length > 0) {
      const previousTotalClients = previousStudiosData.reduce(
        (sum, studioData) => sum + (studioData.analytics.metrics.total_clients_count || 0),
        0
      )
      totalMetrics.clients_growth = totalMetrics.total_clients_count - previousTotalClients
    }

    // Средний чек новых продаж считаем без членских взносов: взнос анализируется отдельной метрикой.
    const totalNewCount = totalMetrics.total_sales || 0
    const totalNewAmount = (totalMetrics.new_sales_amount || 0) - (totalMetrics.membership_fees_new_amount || 0)
    totalMetrics.avg_sale_amount = totalNewCount > 0 ? totalNewAmount / totalNewCount : 0
    totalMetrics.conversion_rate = totalMetrics.total_visitors > 0
      ? (totalNewCount / totalMetrics.total_visitors) * 100
      : 0

    return { metrics: totalMetrics }
  }

  /**
   * Загружает и агрегирует данные нескольких студий (устаревший N-запросный метод)
   * Используется для одиночной студии через loadPeriodAnalytics
   */
  async loadMultiStudioAnalytics(
    studioIds: number[],
    period: MetricPeriod
  ): Promise<AggregatedAnalyticsData> {
    // Делегируем в батчевый метод
    return this.loadMultiStudioAnalyticsBatch(studioIds, period)
  }

  /**
   * Группирует массив строк по studio_internal_id
   */
  private groupByStudio<T extends { studio_internal_id: number }>(rows: T[]): Map<number, T[]> {
    const map = new Map<number, T[]>()
    for (const row of rows) {
      const existing = map.get(row.studio_internal_id)
      if (existing) {
        existing.push(row)
      } else {
        map.set(row.studio_internal_id, [row])
      }
    }
    return map
  }

  /**
   * Батчевая загрузка аналитики нескольких студий.
   * Вместо 8N запросов делает 8 батчевых запросов через .in().
   *
   * Также возвращает сырые данные (weeklyPlans, periodTotals per studio)
   * для использования в useGlobalWeeklyPlanDiagnostics без дублирования запросов.
   */
  async loadMultiStudioAnalyticsBatch(
    studioIds: number[],
    period: MetricPeriod
  ): Promise<AggregatedAnalyticsData & {
    rawWeeklyPlans?: any[]
    rawPeriodTotals?: any[]
    rawAggregatedWeeklyPlans?: any[]
  }> {
    if (studioIds.length === 0) {
      throw new Error('No studios provided')
    }

    // Определяем предыдущий период
    const previousPeriod = await this.getPreviousMetricPeriod(period)
    const prevPeriodId = previousPeriod?.period_id ?? null

    // Рассчитываем даты для conversion tail
    const tailDays = Math.max(0, 7 - 1) // windowSize=7
    const tailStart = this.getDateDaysBefore(period.period_start, tailDays)
    const headDays = 2
    const headEnd = this.getDateDaysAfter(period.period_end, headDays)

    // === Параллельные батчевые запросы ===
    // Per-studio (нужно для пер-студийных вычислений: funnel, marketing, planData каждой студии):
    //   1. period_totals (текущий / прошлый период), 2. weekly_plan_vs_fact (текущий / прошлый),
    //   3. daily_totals (текущий / прошлый).
    // Aggregate (server-side SUM, без полей-по-полю в JS):
    //   4. get_aggregated_daily_totals по полному диапазону [tailStart, headEnd] —
    //      покрывает dailyData / conversionDailyData / nextPeriodHeadData одним запросом.
    //   5. get_aggregated_weekly_plan_vs_fact — для глобального план-факта в
    //      useGlobalWeeklyPlanDiagnostics (SQL обрабатывает plan-no-fact / fact-no-plan).
    const aggregatedDailyFrom = tailDays > 0 ? tailStart : period.period_start
    const aggregatedDailyTo = headDays > 0 ? headEnd : period.period_end

    const [
      periodTotalsRes,
      prevPeriodTotalsRes,
      weeklyPlansRes,
      prevWeeklyPlansRes,
      dailyDataRes,
      prevDailyDataRes,
      aggregatedDailyRes,
      aggregatedWeeklyRes,
      snapshotWindowRes
    ] = await this.withTimeout(
      Promise.all([
        supabase
          .from('studio_period_totals')
          .select('*')
          .in('studio_internal_id', studioIds)
          .eq('period_id', period.period_id),
        prevPeriodId
          ? supabase
              .from('studio_period_totals')
              .select('*')
              .in('studio_internal_id', studioIds)
              .eq('period_id', prevPeriodId)
          : Promise.resolve({ data: [] as any[], error: null }),
        supabase
          .from('studio_weekly_plan_vs_fact')
          .select('*')
          .in('studio_internal_id', studioIds)
          .eq('period_id', period.period_id)
          .order('week_number_in_period'),
        prevPeriodId
          ? supabase
              .from('studio_weekly_plan_vs_fact')
              .select('*')
              .in('studio_internal_id', studioIds)
              .eq('period_id', prevPeriodId)
              .order('week_number_in_period')
          : Promise.resolve({ data: [] as any[], error: null }),
        supabase
          .from('studio_daily_totals')
          .select('*')
          .in('studio_internal_id', studioIds)
          .eq('period_id', period.period_id)
          .order('metric_date'),
        prevPeriodId
          ? supabase
              .from('studio_daily_totals')
              .select('*')
              .in('studio_internal_id', studioIds)
              .eq('period_id', prevPeriodId)
              .order('metric_date')
          : Promise.resolve({ data: [] as any[], error: null }),
        (supabase as any).rpc('get_aggregated_daily_totals', {
          p_studio_ids: studioIds,
          p_date_from: aggregatedDailyFrom,
          p_date_to: aggregatedDailyTo
        }),
        (supabase as any).rpc('get_aggregated_weekly_plan_vs_fact', {
          p_studio_ids: studioIds,
          p_period_id: period.period_id
        }),
        supabase
          .from('studio_daily_totals')
          .select('studio_internal_id, metric_date, total_clients_count, active_subscribers_count, active_visitors_count, active_visitors_28d_count, base_visitors_21d_count, base_visitors_28d_count')
          .in('studio_internal_id', studioIds)
          .gte('metric_date', aggregatedDailyFrom)
          .lte('metric_date', aggregatedDailyTo)
          .order('metric_date')
      ]),
      20000,
      `loadMultiStudioAnalyticsBatch(studios:${studioIds.length})`
    )

    // Проверяем ошибки критических запросов
    if (periodTotalsRes.error) {
      throw new Error(`Failed to batch load period totals: ${periodTotalsRes.error.message}`)
    }
    if (weeklyPlansRes.error) {
      throw new Error(`Failed to batch load weekly plans: ${weeklyPlansRes.error.message}`)
    }
    if (dailyDataRes.error) {
      throw new Error(`Failed to batch load daily data: ${dailyDataRes.error.message}`)
    }
    if (prevPeriodTotalsRes.error) {
      throw new Error(`Failed to batch load previous period totals: ${prevPeriodTotalsRes.error.message}`)
    }
    if (prevWeeklyPlansRes.error) {
      throw new Error(`Failed to batch load previous weekly plans: ${prevWeeklyPlansRes.error.message}`)
    }
    if (prevDailyDataRes.error) {
      throw new Error(`Failed to batch load previous daily data: ${prevDailyDataRes.error.message}`)
    }
    if (aggregatedDailyRes.error) {
      throw new Error(`Failed to batch load aggregated daily totals: ${aggregatedDailyRes.error.message}`)
    }
    if (aggregatedWeeklyRes.error) {
      throw new Error(`Failed to batch load aggregated weekly plan-vs-fact: ${aggregatedWeeklyRes.error.message}`)
    }
    if (snapshotWindowRes.error) {
      throw new Error(`Failed to batch load snapshot metrics: ${snapshotWindowRes.error.message}`)
    }

    const periodTotalsData = (periodTotalsRes.data || []) as any[]
    const prevPeriodTotalsData = (prevPeriodTotalsRes.data || []) as any[]
    const weeklyPlansData = (weeklyPlansRes.data || []) as any[]
    const prevWeeklyPlansData = (prevWeeklyPlansRes.data || []) as any[]
    const dailyDataAll = (dailyDataRes.data || []) as any[]
    const prevDailyDataAll = (prevDailyDataRes.data || []) as any[]
    const aggregatedDailyAll = (aggregatedDailyRes.data || []) as DailyTotals[]
    const aggregatedWeeklyAll = (aggregatedWeeklyRes.data || []) as any[]
    const snapshotWindowRows = (snapshotWindowRes.data || []) as DailyTotals[]

    const periodTotalsByStudio = new Map<number, any>()
    for (const row of periodTotalsData) {
      periodTotalsByStudio.set(row.studio_internal_id, row)
    }

    const prevPeriodTotalsByStudio = new Map<number, any>()
    for (const row of prevPeriodTotalsData) {
      prevPeriodTotalsByStudio.set(row.studio_internal_id, row)
    }

    const weeklyPlansByStudio = this.groupByStudio(weeklyPlansData)
    const prevWeeklyPlansByStudio = this.groupByStudio(prevWeeklyPlansData)
    const dailyDataByStudio = this.groupByStudio(dailyDataAll)
    const prevDailyDataByStudio = this.groupByStudio(prevDailyDataAll)
    const snapshotBaselinesByStudio = buildSnapshotBaselinesByStudio(
      prevDailyDataAll.filter((row) => row.metric_date < aggregatedDailyFrom)
    )
    const aggregatedSnapshotsByDate = aggregateSnapshotMetricsByDate(snapshotWindowRows, {
      studioIds,
      baselinesByStudio: snapshotBaselinesByStudio
    })
    const aggregatedDailyWithSnapshots = aggregatedDailyAll.map((row) => {
      const snapshots = aggregatedSnapshotsByDate.get(row.metric_date)
      if (!snapshots) {
        return row
      }

      return {
        ...row,
        total_clients_count: snapshots.total_clients_count ?? undefined,
        active_subscribers_count: snapshots.active_subscribers_count ?? undefined,
        active_visitors_count: snapshots.active_visitors_count ?? undefined,
        active_visitors_28d_count: snapshots.active_visitors_28d_count ?? undefined,
        base_visitors_21d_count: snapshots.base_visitors_21d_count ?? undefined,
        base_visitors_28d_count: snapshots.base_visitors_28d_count ?? undefined
      }
    })

    // Слайсим серверный агрегат по дням на три окна, которые ожидает дашборд.
    // Сравниваем по строковым ISO-датам — это корректно, так как и RPC, и period.* в формате YYYY-MM-DD.
    const aggregatedDailyData: DailyTotals[] = []
    const aggregatedConversionDailyData: DailyTotals[] = []
    const aggregatedNextPeriodHeadData: DailyTotals[] = []
    for (const row of aggregatedDailyWithSnapshots) {
      const d = row.metric_date
      if (d < period.period_start) {
        aggregatedConversionDailyData.push(row)
      } else if (d <= period.period_end) {
        aggregatedDailyData.push(row)
        aggregatedConversionDailyData.push(row)
      } else {
        aggregatedNextPeriodHeadData.push(row)
      }
    }

    // === Собираем StudioAnalyticsData для каждой студии из батчевых результатов ===
    const studiosData: StudioAnalyticsData[] = []
    const successfulStudioIds: number[] = []

    for (const studioId of studioIds) {
      try {
        // Period totals
        const totals = periodTotalsByStudio.get(studioId) || {}
        const previousPeriodTotals = prevPeriodTotalsByStudio.get(studioId) || null

        // Plan data (из weekly plans)
        const studioWeeklyPlans = (weeklyPlansByStudio.get(studioId) || []) as WeeklyPlanVsFact[]
        const planData = this.buildPlanDataFromWeeklyPlans(studioWeeklyPlans, period.period_id)

        // Daily data (per-studio: нужно для funnel и marketing per-studio).
        // conversionDailyData / nextPeriodHeadData per-studio здесь не строим —
        // на дашборде они используются только в агрегированном виде, который теперь
        // приходит из RPC get_aggregated_daily_totals.
        const dailyData = (dailyDataByStudio.get(studioId) || []) as DailyTotals[]

        // Funnel
        const funnelData = this.calculateFunnelData(dailyData)

        // Previous period plan + marketing budget
        const prevWeeklyPlans = (prevWeeklyPlansByStudio.get(studioId) || []) as WeeklyPlanVsFact[]
        const prevPlanData = prevPeriodId
          ? this.buildPlanDataFromWeeklyPlans(prevWeeklyPlans, prevPeriodId)
          : null

        let previousPeriodData: PlanData | null = null
        if (prevPlanData) {
          const prevDaily = (prevDailyDataByStudio.get(studioId) || []) as DailyTotals[]
          const prevMarketingBudget = this.calculateMarketingExpenses(prevDaily)
          previousPeriodData = { ...prevPlanData, marketing_budget_fact: prevMarketingBudget }
        }

        // Marketing expenses
        const marketingExpenses = this.calculateMarketingExpenses(dailyData)

        // Metrics
        const metrics = this.formatMetrics(totals, marketingExpenses, previousPeriodTotals)

        studiosData.push({
          analytics: { metrics },
          dailyData,
          conversionDailyData: [],
          nextPeriodHeadData: [],
          planData,
          previousPeriodData,
          funnelData
        })
        successfulStudioIds.push(studioId)
      } catch (err) {
        console.error(`Failed to process analytics for studio ${studioId}:`, err)
      }
    }

    if (studiosData.length === 0) {
      throw new Error('Failed to load analytics for all studios')
    }

    // === Агрегация ===
    // dailyData/conversionDailyData/nextPeriodHeadData уже агрегированы сервером выше
    // (RPC get_aggregated_daily_totals, разрезанный на три окна по metric_date).
    const aggregatedPlanData = this.aggregatePlanData(studiosData, period.period_id)

    const previousTotalClients = studiosData.reduce((sum, sd) => {
      return sum + (sd.previousPeriodData?.clients_at_end_fact || 0)
    }, 0)

    const mockPreviousStudiosData: StudioAnalyticsData[] = previousTotalClients > 0
      ? [{
          analytics: {
            metrics: {
              total_clients_count: previousTotalClients
            } as any
          },
          dailyData: [],
          conversionDailyData: [],
          nextPeriodHeadData: [],
          planData: null,
          previousPeriodData: null,
          funnelData: { leads: 0, appointments: 0, visits: 0, sales: 0 }
        }]
      : []

    const aggregatedMetrics = this.aggregateMetrics(studiosData, mockPreviousStudiosData)
    const aggregatedFunnelData = this.calculateFunnelData(aggregatedDailyData)

    const previousPeriodDatas = studiosData
      .map(sd => sd.previousPeriodData)
      .filter((data): data is PlanData => data !== null)

    const aggregatedPreviousPeriodData = previousPeriodDatas.length > 0
      ? this.aggregatePlanData(
          studiosData.map(sd => ({ ...sd, planData: sd.previousPeriodData })),
          previousPeriodDatas[0].period_id
        )
      : null

    return {
      analytics: aggregatedMetrics,
      dailyData: aggregatedDailyData,
      conversionDailyData: aggregatedConversionDailyData,
      nextPeriodHeadData: aggregatedNextPeriodHeadData,
      planData: aggregatedPlanData,
      previousPeriodData: aggregatedPreviousPeriodData,
      funnelData: aggregatedFunnelData,
      studiosCount: studiosData.length,
      studioIds: successfulStudioIds,
      // Per-studio raw данные — нужны useGlobalWeeklyPlanDiagnostics для пер-студийных
      // диагностик (missingPlanStudios / zeroRevenueStudios / zeroPlanRevenueStudios).
      // Сама агрегация недельного план-факта приходит уже посчитанной из SQL ниже.
      rawWeeklyPlans: weeklyPlansData,
      rawPeriodTotals: periodTotalsData,
      // Готовый агрегированный недельный план-факт из RPC (COALESCE+SUM на стороне БД).
      rawAggregatedWeeklyPlans: aggregatedWeeklyAll
    }
  }

  /**
   * Строит PlanData из массива недельных планов (переиспользуемая логика из loadPlanData)
   */
  private buildPlanDataFromWeeklyPlans(weeklyPlans: WeeklyPlanVsFact[], periodId: number): PlanData | null {
    if (!weeklyPlans || weeklyPlans.length === 0) {
      return null
    }

    const firstWeek = weeklyPlans[0]
    const aggregate = (field: string) =>
      weeklyPlans.reduce((sum: number, w: any) => sum + (w[field] || 0), 0)

    const ratioConversion = (numeratorField: string, denominatorField: string) => {
      const denominator = aggregate(denominatorField)
      return denominator > 0 ? (aggregate(numeratorField) / denominator) * 100 : 0
    }
    const salesConversionPlan = (() => {
      const visitorsPlan = aggregate('weekly_new_visitors_plan')
      const salesPlan = aggregate('weekly_new_sales_plan') + aggregate('weekly_new_subscriptions_count_plan')
      return visitorsPlan > 0 ? (salesPlan / visitorsPlan) * 100 : 0
    })()

    return {
      period_id: periodId,
      period_start: firstWeek.week_start,
      period_end: weeklyPlans[weeklyPlans.length - 1].week_end,
      weeks_in_period: weeklyPlans.length,

      leads_count_plan: aggregate('weekly_leads_plan'),
      appointments_count_plan: aggregate('weekly_appointments_plan'),
      new_visitors_count_plan: aggregate('weekly_new_visitors_plan'),
      new_sales_count_plan: aggregate('weekly_new_sales_plan'),
      new_subscriptions_count_plan: aggregate('weekly_new_subscriptions_count_plan'),
      new_sales_amount_plan: aggregate('weekly_new_sales_amount_plan'),
      new_subscriptions_amount_plan: aggregate('weekly_new_subscriptions_amount_plan'),
      membership_fees_new_plan: aggregate('weekly_membership_fees_new_plan'),
      membership_fees_subscription_upgrades_plan: aggregate('weekly_membership_fees_subscription_upgrades_plan'),
      membership_fees_subscription_returning_plan: aggregate('weekly_membership_fees_subscription_returning_plan'),
      subscription_renewals_count_plan: aggregate('weekly_subscription_renewals_count_plan'),
      subscription_renewals_amount_plan: aggregate('weekly_subscription_renewals_amount_plan'),
      subscription_upgrades_count_plan: aggregate('weekly_subscription_upgrades_count_plan'),
      subscription_upgrades_amount_plan: aggregate('weekly_subscription_upgrades_amount_plan'),
      subscription_returning_clients_count_plan: aggregate('weekly_subscription_returning_clients_count_plan'),
      subscription_returning_clients_amount_plan: aggregate('weekly_subscription_returning_clients_amount_plan'),
      renewals_count_plan: aggregate('weekly_renewals_count_plan'),
      renewals_amount_plan: aggregate('weekly_renewals_amount_plan'),
      upgrades_count_plan: aggregate('weekly_upgrades_count_plan'),
      upgrades_amount_plan: aggregate('weekly_upgrades_amount_plan'),
      returning_clients_count_plan: aggregate('weekly_returning_clients_count_plan'),
      returning_clients_amount_plan: aggregate('weekly_returning_clients_amount_plan'),
      refunds_amount_plan: aggregate('weekly_refunds_amount_plan'),
      total_gross_revenue_plan: aggregate('weekly_gross_revenue_plan'),
      marketing_budget_plan: aggregate('weekly_marketing_budget_plan'),

      avg_new_sales_check_plan: (() => {
        const count = aggregate('weekly_new_sales_plan')
        const amount = aggregate('weekly_new_sales_amount_plan')
        return count > 0 ? amount / count : (firstWeek.weekly_new_sales_aov_plan || 0)
      })(),
      avg_new_subscription_check_plan: (() => {
        const count = aggregate('weekly_new_subscriptions_count_plan')
        const amount = aggregate('weekly_new_subscriptions_amount_plan')
        return count > 0 ? amount / count : (firstWeek.weekly_new_subscriptions_aov_plan || 0)
      })(),
      avg_recurring_sales_check_plan: (() => {
        const count =
          aggregate('weekly_renewals_count_plan') +
          aggregate('weekly_upgrades_count_plan') +
          aggregate('weekly_returning_clients_count_plan') +
          aggregate('weekly_subscription_renewals_count_plan') +
          aggregate('weekly_subscription_upgrades_count_plan') +
          aggregate('weekly_subscription_returning_clients_count_plan')
        const amount =
          aggregate('weekly_renewals_amount_plan') +
          aggregate('weekly_upgrades_amount_plan') +
          aggregate('weekly_returning_clients_amount_plan') +
          aggregate('weekly_subscription_renewals_amount_plan') +
          aggregate('weekly_subscription_upgrades_amount_plan') +
          aggregate('weekly_subscription_returning_clients_amount_plan')
        return count > 0 ? amount / count : 0
      })(),

      booking_conversion_plan: ratioConversion('weekly_appointments_plan', 'weekly_leads_plan'),
      visit_conversion_plan: ratioConversion('weekly_new_visitors_plan', 'weekly_appointments_plan'),
      sales_conversion_plan: salesConversionPlan,

      leads_count_fact: aggregate('weekly_leads_fact'),
      appointments_count_fact: aggregate('weekly_appointments_fact'),
      new_visitors_count_fact: aggregate('weekly_new_visitors_fact'),
      new_sales_count_fact: aggregate('weekly_new_sales_fact'),
      new_subscriptions_count_fact: aggregate('weekly_new_subscriptions_count_fact'),
      new_sales_amount_fact: aggregate('weekly_new_sales_amount_fact'),
      new_subscriptions_amount_fact: aggregate('weekly_new_subscriptions_amount_fact'),
      membership_fees_new_amount_fact: aggregate('weekly_membership_fees_new_amount_fact'),
      membership_fees_subscription_upgrades_amount_fact: aggregate('weekly_membership_fees_subscription_upgrades_amount_fact'),
      membership_fees_subscription_returning_amount_fact: aggregate('weekly_membership_fees_subscription_returning_amount_fact'),
      subscription_renewals_count_fact: aggregate('weekly_subscription_renewals_count_fact'),
      subscription_renewals_amount_fact: aggregate('weekly_subscription_renewals_amount_fact'),
      subscription_upgrades_count_fact: aggregate('weekly_subscription_upgrades_count_fact'),
      subscription_upgrades_amount_fact: aggregate('weekly_subscription_upgrades_amount_fact'),
      subscription_returning_clients_count_fact: aggregate('weekly_subscription_returning_clients_count_fact'),
      subscription_returning_clients_amount_fact: aggregate('weekly_subscription_returning_clients_amount_fact'),
      renewals_count_fact: aggregate('weekly_renewals_count_fact'),
      renewals_amount_fact: aggregate('weekly_renewals_amount_fact'),
      upgrades_count_fact: aggregate('weekly_upgrades_count_fact'),
      upgrades_amount_fact: aggregate('weekly_upgrades_amount_fact'),
      returning_clients_count_fact: aggregate('weekly_returning_clients_count_fact'),
      returning_clients_amount_fact: aggregate('weekly_returning_clients_amount_fact'),
      refunds_amount_fact: aggregate('weekly_refunds_amount_fact'),
      total_gross_revenue_fact: aggregate('weekly_gross_revenue_fact'),
      marketing_budget_fact: aggregate('weekly_marketing_expenses_amount_fact')
    } as PlanData
  }

  // ===== НОВЫЕ МЕТОДЫ: Работа с недельными данными =====

  /**
   * Загружает недельное сравнение план vs факт
   * Использует VIEW studio_weekly_plan_vs_fact
   * 
   * @param studioId - ID студии
   * @param periodId - ID периода
   * @returns Массив недельных данных план vs факт
   */
  async loadWeeklyPlanVsFact(studioId: number, periodId: number) {
    const { data, error } = await supabase
      .from('studio_weekly_plan_vs_fact')
      .select('*')
      .eq('studio_internal_id', studioId)
      .eq('period_id', periodId)
      .order('week_number_in_period')
    
    if (error) {
      throw new Error(`Failed to load weekly plan vs fact: ${error.message}`)
    }
    
    return data || []
  }

  /**
   * Загружает недельные факты без плана
   * Использует VIEW studio_weekly_totals
   * 
   * @param studioId - ID студии
   * @param periodId - ID периода
   * @returns Массив недельных фактических данных
   */
  async loadWeeklyTotals(studioId: number, periodId: number) {
    const { data, error } = await supabase
      .from('studio_weekly_totals')
      .select('*')
      .eq('studio_internal_id', studioId)
      .eq('period_id', periodId)
      .order('week_number_in_period')
    
    if (error) {
      throw new Error(`Failed to load weekly totals: ${error.message}`)
    }
    
    return data || []
  }

  /**
   * Агрегирует недельные метрики в месячные
   * Полезно для сравнения с месячными планами
   * 
   * @param weeks - Массив недельных данных
   * @returns Агрегированные месячные метрики
   */
  aggregateWeeklyMetrics(weeks: any[]) {
    if (!weeks || weeks.length === 0) {
      return null
    }

    const aggregate = (field: string) => 
      weeks.reduce((sum, w) => sum + (w[field] || 0), 0)

    const weeksWithData = weeks.filter(w => w.days_with_data > 0).length

    return {
      // План (сумма недельных планов)
      total_leads_plan: aggregate('weekly_leads_plan'),
      total_appointments_plan: aggregate('weekly_appointments_plan'),
      total_new_visitors_plan: aggregate('weekly_new_visitors_plan'),
      total_new_sales_plan: aggregate('weekly_new_sales_plan'),
      total_new_sales_amount_plan: aggregate('weekly_new_sales_amount_plan'),
      total_gross_revenue_plan: aggregate('weekly_gross_revenue_plan'),
      
      // Факт (сумма недельных фактов)
      total_leads_fact: aggregate('weekly_leads_fact'),
      total_appointments_fact: aggregate('weekly_appointments_fact'),
      total_new_visitors_fact: aggregate('weekly_new_visitors_fact'),
      total_new_sales_fact: aggregate('weekly_new_sales_fact'),
      total_new_sales_amount_fact: aggregate('weekly_new_sales_amount_fact'),
      total_gross_revenue_fact: aggregate('weekly_gross_revenue_fact'),
      
      // Метаданные
      weeks_with_data: weeksWithData,
      total_weeks: weeks.length,
      
      // Средние значения (для конверсий и AOV)
      avg_booking_conversion_fact: weeks
        .filter(w => w.booking_conversion_fact !== null)
        .reduce((sum, w, _, arr) => sum + w.booking_conversion_fact / arr.length, 0),
      
      avg_visit_conversion_fact: weeks
        .filter(w => w.visit_conversion_fact !== null)
        .reduce((sum, w, _, arr) => sum + w.visit_conversion_fact / arr.length, 0),
      
      avg_sales_conversion_fact: weeks
        .filter(w => w.sales_conversion_fact !== null)
        .reduce((sum, w, _, arr) => sum + w.sales_conversion_fact / arr.length, 0),
    }
  }
}

// Экспортируем единственный экземпляр сервиса (singleton)
export const analyticsService = new AnalyticsService()
